import { Inject, Options, Vue, Watch } from "vue-property-decorator";
import { TrustAPI } from "@/shared/modules/trust-api";
import { AppInsights } from "@/trust-atcs-app/appInsights";
import store from "@/trust-atcs-app/store";
import { EventEmitter } from "events";
import HelpScreen from "../help-screen/index.vue";
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import { RouteN } from "@/trust-atcs-app/constants/router-name";
import { EeReport, PageContent } from "@/shared/modules/typescript-trustapi-client/models";
import { NavStep, NavStepStatusID, Section, Step } from "../swipe-stepper/nav-step";
import SwipeStepper from "../swipe-stepper/index.vue";
import FormEerOverview from "../form-eer-overview/index.vue";
import FormEerGeneralTrustMoney from "../form-eer-general-trust-money/index.vue";
import FormEerControlledMoney from "../form-eer-controlled-money/index.vue";
import FormEerSpecificPowerMoney from "../form-eer-specific-power-money/index.vue";
import FormEerInvestmentMoney from "../form-eer-investment-money/index.vue";
import FormEerOtherInformation from "../form-eer-other-information/index.vue";
import FormEerDeclaration from "../form-eer-declaration/index.vue";
import FormEerConfirmation from "../form-eer-confirmation/index.vue";
import LoadingIndicator from "@/shared/components/loading-indicator/index.vue";
import ConfirmDialogue from '@/shared/components/confirm-dialogue/index.vue';
import FormHeader from "../form-header/index.vue";
import FormEerBreaches from "../form-eer-breaches/index.vue";
import { FormEERData } from "@/trust-atcs-app/models/form-eer-data";
import LoadingSpinner from "@/shared/components/loading-spinner/index.vue";
import { IDLESessionManager } from '@/trust-atcs-app/modules/session/idle-session-manager';
import SessionExpired from "../session-expired-modal/index.vue";
import { useFormEERDataStore } from "@/trust-atcs-app/store/useFormEerDataStore";

@Options({
  components: {
    LoadingIndicator,
    LoadingSpinner,
    SwipeStepper,
    FormHeader,
    HelpScreen,
    FormEerOverview,
    FormEerGeneralTrustMoney,
    FormEerControlledMoney,
    FormEerSpecificPowerMoney,
    FormEerInvestmentMoney,
    FormEerOtherInformation,
    FormEerBreaches,
    FormEerDeclaration,
    FormEerConfirmation,
    ConfirmDialogue,
    SessionExpired
  },
  mixins: [FormatDateTimeMixin],
})
export default class FormEer extends Vue {
  private formEERDataStore = useFormEERDataStore();

  private ai = new AppInsights;
  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private navSteps = [
    new NavStep(Step.overview, Section.overview), // 0
    new NavStep(Step.generalTrustMoney, Section.generalTrustMoney), // 1
    new NavStep(Step.controlledMoney, Section.controlledMoney), // 2
    new NavStep(Step.specificPowerMoney, Section.specificPowerMoney), // 3
    new NavStep(Step.investmentMoney, Section.investmentMoney), // 4
    new NavStep(Step.otherInformation, Section.otherInformation), // 5
    new NavStep(Step.breaches, Section.breaches), // 6
    new NavStep(Step.declaration, Section.declaration), // 7
    new NavStep(Step.confirmation, Section.confirmation), // 8
  ];

  private backButtonText = 'Cancel Application';
  private nextButtonText = 'Next';
  private backButtonVisible = true;

  private formName = { line1: '', line2: 'Report' };
  private formDesc = { line1: 'External Examiners Report', line2: '' };

  private isSectionLoading = false;
  private isSaving = false;
  private stopExtending = false;
  private showSessionExpiredModal = false;
  private workerID = -1;

  private sectionName = '';
  private helpContent: PageContent = {};

  get $Section() {
    return Section;
  }

  private validSection: boolean[] = [
    true, // overview
    false, // generalTrustMoney
    false, // controlledMoney
    false, // specificPowerMoney
    false, // investmentMoney
    false, // otherInformation
    false, // breaches
    false, // declaration
    true, // confirmation
  ];

  private formEERData: FormEERData = {
    overview: null as any,
    generalTrustMoney: null as any,
    controlledMoney: null as any,
    specificPowerMoney: null as any,
    investmentMoney: null as any,
    otherInformation: null as any,
    breaches: null as any,
    declarationDetails: null as any,
  };

  private idleSessionMgr: IDLESessionManager = new IDLESessionManager(60);

  @Watch('$route')
  public OnRouteChange(val: any, oldVal: any): void {
    const _stored = 'something' //check store data here
    window.scrollTo(0, 0);
    if (oldVal.fullPath.indexOf('confirmation') > -1 || _stored == null) {
      this.$router.push({ name: RouteN.ATCS.DashboardMain.n });
    }
    this.setCurrentStep();
  }

  private setFormDescription(message: any) {
    if (message) {
      this.formDesc = { line1: 'External Examiner Report as at ' + message, line2: '' };
    }
  }

  created(): void {
    this.idleSessionMgr.run(this.idleSessionTimeoutCallback);
    this.workerID = setInterval(() => {
      this.extendSession();
    }, 30000);
  }

  mounted(): void {
    window.scrollTo(0, 0);
    this.eventCtrl.on('save-and-close', this.saveAndClose);
    this.eventCtrl.on('save-current', this.saveCurrent);
    this.eventCtrl.on('save-section', this.saveSection);
    this.setCurrentStep();
    this.formEERDataStore.setFormEERData(this.formEERData);
  }

  beforeUnmount(): void {
    clearInterval(this.workerID);
    this.eventCtrl.off('save-and-close', this.saveAndClose);
    this.eventCtrl.off('save-current', this.saveCurrent);
    this.eventCtrl.off('save-section', this.saveSection);
  }

  beforeRouteLeave(to: any, from: any, next: any): void {
    store.dispatch('setSelectedComplianceFormEER', null);
    store.dispatch('setFormUpdatingkey', null);
    store.dispatch('setFormEERData', {});
    this.formEERDataStore.clearFormEERData();
  }

  private saveCurrent(): void {
    this.saveSection(false, true);
  }

  private exitForm(): void {
    this.stopExtending = true;
    clearInterval(this.workerID);
    this.$router.push({ name: RouteN.ATCS.DashboardHome.n });
    this.showSessionExpiredModal = false;
  }

  private idleSessionTimeoutCallback(): void {
    this.stopExtending = true;
  }

  private async extendSession(): Promise<void> {
    try {
      if (this.stopExtending) {
        clearInterval(this.workerID);
        this.showSessionExpiredModal = true;
        return;
      }
      if (this.isSaving) {
        return;
      }
      await this.apiService.EeReportApi?.apiV2EeReportExtendFormStatusPost({
        complianceFormId: this.complianceFormEER.id,
        updatingKey:this.formUpdatingKey
      });

    } catch (err) {
      clearInterval(this.workerID);
      this.showSessionExpiredModal = true;
    }
  }

  private setCurrentStep(): void {
    const _section: string = this.$route.query.sec as string;
    if (_section !== Section.overview) {
      this.backButtonText = 'Back';
    } else {
      this.backButtonText = 'Cancel';
    }

    this.backButtonVisible = true;
    this.nextButtonText = 'Next';
    switch (_section) {
      case Section.overview:
        this.backButtonVisible = false;
        this.setSteps(0);
        break;
      case Section.generalTrustMoney:
        this.setSteps(1);
        break;
      case Section.controlledMoney:
        this.setSteps(2);
        break;
      case Section.specificPowerMoney:
        this.setSteps(3);
        break;
      case Section.investmentMoney:
        this.setSteps(4);
        break;
      case Section.otherInformation:
        this.setSteps(5);
        break;
      case Section.breaches:
        this.setSteps(6);
        break;
      case Section.declaration:
        this.setSteps(7);
        this.nextButtonText = 'Submit';
        break;
      case Section.confirmation:
        this.setSteps(8);
        this.nextButtonText = 'Close';
        this.backButtonVisible = false;
        break;
      default:
        break;
    }
  }

  private setSteps(n: number) {
    this.updateHelpContent(n);
    for (let i = 0; i < this.navSteps.length; i++) {
      this.navSteps[i].status = NavStepStatusID.NotDone
    }
    for (let i = 0; i < n; i++) {
      this.navSteps[i].status = NavStepStatusID.Done
    }
    this.navSteps[n].status = NavStepStatusID.Current
    // the confirmation page
    const _confirmation = this.navSteps.length - 1;
    if (n === _confirmation) {
      for (let i = 0; i < _confirmation; i++) {
        this.navSteps[i].status = NavStepStatusID.Unknown
      }
    } else {
      // save the current step
      // window.sessionStorage.setItem('contractData', JSON.stringify(this.contractData));
    }
  }

  private updateHelpContent(n: number) {
    switch (n) {
      case 0: // Section.overview
        this.helpContent = this.$getPopover('Help', 18);
        break;
      case 1: // Section.generalTrustMoney
        this.helpContent = this.$getPopover('Help', 19);
        break;
      case 2: // Section.controlledMoney
        this.helpContent = this.$getPopover('Help', 20);
        break;
      case 3: // Section.specificPowerMoney
        this.helpContent = this.$getPopover('Help', 21);
        break;
      case 4: // Section.investmentMoney 
        this.helpContent = this.$getPopover('Help', 22);
        break;
      case 5: // Section.otherInformation
        this.helpContent = this.$getPopover('Help', 23);
        break;
      case 6: // Section.breaches
        this.helpContent = this.$getPopover('Help', 26);
        break;
      case 7: // Section.declaration
        this.helpContent = this.$getPopover('Help', 24);
        break;
      case 8: // Section.confirmation
        this.helpContent = this.$getPopover('Help', 25);
        break;
      default:
        this.helpContent = {};
        break;
    }
    this.eventCtrl.emit('updateHelpContent', this.helpContent);
  }

  private async goBack() {
    const _section: string = this.$route.query.sec as string;
    await this.apiService.EeReportApi?.apiV2EeReportExtendFormStatusPost({
      complianceFormId: this.complianceFormEER.id,
      updatingKey: this.formUpdatingKey,
    }).then(() => {
      switch (_section) {
        case Section.overview:
          // this.eventCtrl.emit('cancel-pc-renew');
          // this.$router.push({ name: RouteN.Mars.PcRenewOverview.n, query: { appId: this.$route.query.appId } });
          break;
        case Section.generalTrustMoney:
          this.navigate(this.navSteps[0]);
          break;
        case Section.controlledMoney:
          this.navigate(this.navSteps[1]);
          break;
        case Section.specificPowerMoney:
          this.navigate(this.navSteps[2]);
          break;
        case Section.investmentMoney:
          this.navigate(this.navSteps[3]);
          break;
        case Section.otherInformation:
          this.navigate(this.navSteps[4]);
          break;
        case Section.breaches:
          this.navigate(this.navSteps[5]);
          break;
        case Section.declaration:
          this.navigate(this.navSteps[6]);
          break;
        case Section.confirmation:
          // this.eventCtrl.emit('cancel-eer');
          break;
        default:
          break;
      }
    }).catch((err: any) => {
      this.showSessionExpiredModal = true;
    });
    
  }

  private navigate(s: NavStep): void {
    const temp: string = JSON.stringify(this.$route.query);
    const tempJSON = JSON.parse(temp);
    tempJSON.sec = s.routeSecName;
    this.$router.push({ query: tempJSON });
  }

  private goNext() {
    // store.dispatch('setPcrFormData', this.pcrFormData);
    const _section = this.$route.query.sec as string;
    switch (_section) {
      case Section.overview:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[1])) : true).catch((err: any) => console.log(err));
        break;
      case Section.generalTrustMoney:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[2])) : true).catch((err: any) => console.log(err));
        break;
      case Section.controlledMoney:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[3])) : true).catch((err: any) => console.log(err));
        break;
      case Section.specificPowerMoney:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[4])) : true).catch((err: any) => console.log(err));
        break;
      case Section.investmentMoney:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[5])) : true).catch((err: any) => console.log(err));
        break;
      case Section.otherInformation:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[6])) : true).catch((err: any) => console.log(err));
        break;
      case Section.breaches:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[7])) : true).catch((err: any) => console.log(err));
        break;
      case Section.declaration:
        this.saveSection(false, false).then(
          (x: any) => !x ? (this.navigate(this.navSteps[8])) : true).catch((err: any) => console.log(err));
        break;

      case Section.confirmation:
        // this.openSurveyWindow();
        this.$router.push({ name: RouteN.ATCS.DashboardHome.n });
        break;
      default:
        break;
    }

  }

  get currentSection(): string {
    return this.$route.query.sec as string;
  }

  get savingText() {
    return 'Saving...';
  }

  private saveAndClose(): void {
    const _confirm: any = this.$refs.confirmSaveClose;
    _confirm.show({
      title: 'Save & Close',
      message: 'By selecting the \'Save & Close\' button, any answered parts will be saved, and you will be taken back to the Homepage. Would you like to proceed?',
      okButton: 'Proceed',
      cancelButton: 'Cancel',
    }).then((res: any) => {
      if (res) {
        this.saveSection(true, true);
      }
    })
  }


  private async saveSection(close: boolean, skipValidation: boolean): Promise<boolean> {
    try {
      this.isSaving = true;
      const _section = this.$route.query.sec as string;
      await this.apiService.EeReportApi?.apiV2EeReportExtendFormStatusPost({
        complianceFormId: this.complianceFormEER.id,
        updatingKey: this.formUpdatingKey,
      });
      switch (_section) {
        case Section.generalTrustMoney:
          await this.apiService.EeReportApi?.apiV2EeReportSaveGeneralTrustMoneyPost({
            id: this.formEERData?.generalTrustMoney?.id,
            complianceFormStatus: this.formEERData?.generalTrustMoney?.complianceFormStatus,
            atcsMainQuestions: this.formEERData?.generalTrustMoney?.atcsMainQuestions,
            complianceFormId: this.complianceFormEER.id,
            updatingKey: this.formUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-eer-gtm-content');
          break;

        case Section.controlledMoney:
          await this.apiService.EeReportApi?.apiV2EeReportSaveControlledMoneyPost({
            id: this.formEERData?.controlledMoney?.id,
            complianceFormStatus: this.formEERData?.controlledMoney?.complianceFormStatus,
            atcsMainQuestions: this.formEERData?.controlledMoney?.atcsMainQuestions,
            complianceFormId: this.complianceFormEER.id,
            updatingKey: this.formUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-eer-cm-content');
          break;
        case Section.specificPowerMoney:
          await this.apiService.EeReportApi?.apiV2EeReportSaveSpecificPowerMoneyPost({
            id: this.formEERData?.specificPowerMoney?.id,
            complianceFormStatus: this.formEERData?.specificPowerMoney?.complianceFormStatus,
            atcsMainQuestions: this.formEERData?.specificPowerMoney?.atcsMainQuestions,
            complianceFormId: this.complianceFormEER.id,
            updatingKey: this.formUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-eer-spm-content');
          break;
        case Section.investmentMoney:
          await this.apiService.EeReportApi?.apiV2EeReportSaveInvestmentMoneyPost({
            id: this.formEERData?.investmentMoney?.id,
            complianceFormStatus: this.formEERData?.investmentMoney?.complianceFormStatus,
            atcsMainQuestions: this.formEERData?.investmentMoney?.atcsMainQuestions,
            complianceFormId: this.complianceFormEER.id,
            updatingKey: this.formUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-eer-im-content');
          break;
        case Section.otherInformation:
          await this.apiService.EeReportApi?.apiV2EeReportSaveOtherInformationPost({
            id: this.formEERData?.otherInformation?.id,
            complianceFormStatus: this.formEERData?.otherInformation?.complianceFormStatus,
            atcsMainQuestions: this.formEERData?.otherInformation?.atcsMainQuestions,
            complianceFormId: this.complianceFormEER.id,
            updatingKey: this.formUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-eer-oi-content');
          break;

        case Section.breaches:
          await this.apiService.EeReportApi?.apiV2EeReportSaveBreachesPost({
            complianceFormId: this.complianceFormEER.id,
            updatingKey: this.formUpdatingKey,
            breaches: this.formEERData.breaches?.breaches,
            hasBreaches: this.formEERData.breaches?.hasBreaches,
            breachesComment: this.formEERData.breaches?.breachesComment,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-eer-breaches-content');
          break;
        case Section.declaration:
          if (skipValidation && !close) {
            await this.apiService.EeReportApi?.apiV2EeReportSaveDeclarationPost({
              complianceFormId: this.complianceFormEER.id,
              updatingKey: this.formUpdatingKey,
              additionalInformation: this.formEERData.declarationDetails?.additionalInformation,
            });

            if (skipValidation && !close)
              this.eventCtrl.emit('reload-form-eer-declaration-content');

          } else if (!close) {
            await this.apiService.EeReportApi?.apiV2EeReportSubmitEeReportPost({
              complianceFormId: this.complianceFormEER.id,
              updatingKey: this.formUpdatingKey,
              additionalInformation: this.formEERData.declarationDetails?.additionalInformation,
            });
          }
          break;

        default:
          break;
      }

      if (close) {
        // this.isClosing = true;
        await this.apiService.EeReportApi?.apiV2EeReportClosePost({
          complianceFormId: this.complianceFormEER.id,
          updatingKey: this.formUpdatingKey ?? null
        });
        // this.isClosing = false;
        this.$router.push({ name: RouteN.ATCS.DashboardMain.n });
      }
      return Promise.resolve(false);
    } catch (err) {
      // this.isClosing = false;
      // this.showSessionExpiredModal = true;
      console.log('Error saving EER section:', err);
      this.ai.trackException('Error saving EER section: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Error saving section');
      return Promise.reject(true);
    } finally {
      this.isSaving = false;
    }
  }


  private validationForm(i: any, isValid: any) {
    this.validSection[i] = isValid;
    this.canGoNext();
  }

  private canGoNext(): boolean {
    // check if the current section is valid
    const _section = this.$route.query.sec as string;

    switch (_section) {
      case Section.overview:
        return this.validSection[0];
      case Section.generalTrustMoney:
        return this.validSection[1];
      case Section.controlledMoney:
        return this.validSection[2];
      case Section.specificPowerMoney:
        return this.validSection[3];
      case Section.investmentMoney:
        return this.validSection[4];
      case Section.otherInformation:
        return this.validSection[5];
      case Section.breaches:
        return this.validSection[6];
      case Section.declaration:
        return this.validSection[7];
      case Section.confirmation:
        return true;
      default:
        return false;
    }
  }

  get complianceFormEER(): EeReport {
    return store.getters.getSelectedComplianceFormEER;
  }

  get formUpdatingKey(): string {
    return store.getters.getFormUpdatingKey;
  }

}

